import { useEffect } from 'react';

import { BorrowerStatus } from 'enums/curo/BorrowerStatus';
import { DeclineReasons } from 'enums/curo/DeclineReasons';
import { OfferType } from 'enums/curo/OfferType';
import { PreliminaryCheckResult } from 'enums/curo/PreliminaryCheckResult';
import { RefiScoreCheckResult } from 'enums/curo/RefiScoreCheckResult';
import { CuroApplicationVariables } from 'enums/curo/Variables';
import { getApplicationSelector, getSignInProcessState } from 'handlers/curo-handlers/selectors';
import { AccountDataType } from 'handlers/curo-handlers/signInProcessSlice';
import { useAppSelector } from 'hooks/reduxHooks';
import { useAppProgressBar } from 'hooks/useAppProgressBar';
import { useCurrentStep } from 'hooks/useCurrentStep';
import { ApplicationStatuses, ICuroApplication } from 'types/curo/CuroApplication';

import ApplicationProcessSteps from './constants';

const STEPS_WITH_PROGRESS_BAR = [
  ApplicationProcessSteps.VerificationProcessStep, 
  ApplicationProcessSteps.ApplicationRefiScoreStep, 
  ApplicationProcessSteps.ApplicationDetailsSubmissionStep,
];

const getApplicationProcessStep = (
  accountData: AccountDataType,
  application: ICuroApplication,
): ApplicationProcessSteps | null => {
  const isEmailVerified = accountData.isEmailVerified;
  const isPhoneVerified = accountData.isPhoneVerified;
  const applicationStatus = application.status;
  const refiScoreCheckResult = application.variables[CuroApplicationVariables.RefiScoreCheckResult];
  const isLoanDocsSigned = !!application.variables[CuroApplicationVariables.LoanDocsSigned];

  const isAutoPayStepComplete =
    typeof application.variables[CuroApplicationVariables.AutoPay] === 'boolean';

  if (!isEmailVerified || !isPhoneVerified) {
    return ApplicationProcessSteps.VerificationProcessStep;
  }

  if (refiScoreCheckResult === RefiScoreCheckResult.Other) {
    return ApplicationProcessSteps.ApplicationNotFoundPageStep;
  }

  if (applicationStatus === ApplicationStatuses.Decline) {
    const { declineReasons } = application;
    if (
      declineReasons.includes(DeclineReasons.FailedEmailVerification) ||
      declineReasons.includes(DeclineReasons.FailedPhoneVerification)
    ) {
      return ApplicationProcessSteps.FailedVerificationStep;
    }

    if (declineReasons.includes(DeclineReasons.WithdrawnApplication)) {
      return ApplicationProcessSteps.ApplicationIsNotApprovedStep;
    }

    if (declineReasons.includes(DeclineReasons.RefiAbandon)) {
      return ApplicationProcessSteps.RefiApplicationExpiredStep;
    }

    if (declineReasons.includes(DeclineReasons.RefiScore)) {
      return ApplicationProcessSteps.ApplicationIsNotApprovedNOAAStep;
    }
    
    if (declineReasons.includes(DeclineReasons.RefiInvalidState)) {
      return ApplicationProcessSteps.RefiApplicationInvalidStep;
    }

    if (
      declineReasons.includes(DeclineReasons.SummitLead) &&
      application.variables[CuroApplicationVariables.SendSummitLeadEmail]
    ) {
      return ApplicationProcessSteps.ApplicationOnlineOriginationFailedStep;
    }

    if (
      declineReasons.includes(DeclineReasons.SummitLead) &&
      refiScoreCheckResult === RefiScoreCheckResult.SummitLead
    ) {
      return ApplicationProcessSteps.ApplicationRefiScoreDeclinedStep;
    }

    return ApplicationProcessSteps.ApplicationIsNotApprovedNOAAStep;
  }

  if (
    applicationStatus === ApplicationStatuses.NewApplication &&
    !application.variables[CuroApplicationVariables.PreliminaryCheckCompleted]
  ) {
    return ApplicationProcessSteps.ApplicationPreliminaryCheckStep;
  }

  if (
    applicationStatus === ApplicationStatuses.NewApplication &&
    !!application.variables[CuroApplicationVariables.PreliminaryCheckCompleted]
  ) {
    switch (application.variables[CuroApplicationVariables.PreliminaryCheckResult]) {
      case null:
        return ApplicationProcessSteps.ApplicationNotFoundPageStep;
      case PreliminaryCheckResult.Fail:
        return ApplicationProcessSteps.ActiveLoanOrPriorChangeOffPageStep;
      case PreliminaryCheckResult.OrganicTrafficIneligibleZipCode:
        return ApplicationProcessSteps.OrganicTrafficIneligibleZipCode;
      case PreliminaryCheckResult.PrescreenDoesNotMatch:
      case PreliminaryCheckResult.NoPrescreenedOffer:
        return ApplicationProcessSteps.NoPreScreenedOfferFound;
    }
  }

  if (
    applicationStatus === ApplicationStatuses.NewApplication &&
    ((application.variables[CuroApplicationVariables.BorrowerStatus] ===
      BorrowerStatus.Existing &&
      !refiScoreCheckResult) ||
      ([BorrowerStatus.Former, BorrowerStatus.Existing].includes(
        application.variables[CuroApplicationVariables.BorrowerStatus] as BorrowerStatus,
      ) &&
        !application.variables[CuroApplicationVariables.SnowflakeRefiReloanExecuted]))
  ) {
    return ApplicationProcessSteps.ApplicationRefiScoreStep;
  }

  if (applicationStatus === ApplicationStatuses.NewApplication && 
    application.variables[CuroApplicationVariables.OfferType] === OfferType.NoCreditOffer
  ) {
    return ApplicationProcessSteps.GenerateLoanOfferStep;
  }

  if (applicationStatus === ApplicationStatuses.NewApplication) {
    return ApplicationProcessSteps.ApplicationDetailsSubmissionStep;
  }

  if (
    [ApplicationStatuses.Approved, ApplicationStatuses.AutoApproved].includes(applicationStatus) &&
    !isAutoPayStepComplete && 
    !isLoanDocsSigned
  ) {
    return ApplicationProcessSteps.GenerateLoanOfferStep;
  }

  if (
    applicationStatus === ApplicationStatuses.AutoApproved &&
    isAutoPayStepComplete && 
    !isLoanDocsSigned
  ) {
    return ApplicationProcessSteps.SignLoanAgreementStep;
  }

  if (
    isLoanDocsSigned &&
    [
      ApplicationStatuses.SentForFunding,
      ApplicationStatuses.AutoApproved,
      ApplicationStatuses.Approved,
    ].includes(applicationStatus)
  ) {
    return ApplicationProcessSteps.ContactSupportStep;
  }

  if (applicationStatus === ApplicationStatuses.FundingFailed) {
    return ApplicationProcessSteps.FundingFailedStep;
  }

  if (applicationStatus === ApplicationStatuses.Funded) {
    return ApplicationProcessSteps.FundedStep;
  }

  return null;
};

export const useApplicationProcessStep = () => {
  const { resetProgress } = useAppProgressBar();
  const [currentStep, setCurrentStep] = useCurrentStep<ApplicationProcessSteps | null>(null);
  const { accountData } = useAppSelector(getSignInProcessState);
  const application = useAppSelector(getApplicationSelector);

  useEffect(() => {
    if (accountData && application) {
      const step = getApplicationProcessStep(accountData, application);
      setCurrentStep(step);

      if (step && !STEPS_WITH_PROGRESS_BAR.includes(step)) {
        resetProgress();
      }
    }
  }, [accountData, application]);

  return currentStep;
};
